import { makeAutoObservable } from 'mobx';

import { toBaseProductType } from '@/collection/entity/CollectionType';
import { flattenTree } from '@/common/components/Tree/TreeItemView/TreeItemView.utils';
import { Rollout } from '@/common/constants/rollouts';
import { collectSubtreeIds } from '@/common/helpers/tree.utils';
import useStore from '@/common/hooks/useStore';
import { DRIVE_ROOT_NAME } from '@/drive/constants/driveRootName';
import { DriveDestinationCommand, toCommandCollectionRequest } from '@/drive/entities/DriveDestinationCommand';
import { DriveItemType } from '@/drive/entities/DriveItem/DriveItem';
import { DriveItemId, isTemporaryDriveItemId } from '@/drive/entities/DriveItemId';
import { DriveNode } from '@/drive/entities/DriveNode';
import AppSectionTreeItem from '@/drive/entities/DriveTreeItem/AppSectionTreeItem';
import { CollectionTreeItem } from '@/drive/entities/DriveTreeItem/CollectionTreeItem';
import { DriveTreeItem } from '@/drive/entities/DriveTreeItem/DriveTreeItem';
import { FolderTreeItem } from '@/drive/entities/DriveTreeItem/FolderTreeItem';
import RecentDestinationStorage from '@/drive/services/RecentDestinationStorage';
import DriveTreeStore from '@/drive/stores/DriveTreeStore';
import { DriveSection } from '@/drive/types/DriveSection';
import { sortByCreatedAt } from '@/forwarding/components/ForwardModal/ForwardModalBody/RecentDestinations/RecentDestinationsStore.utils';
import ForwardShareStore from '@/forwarding/components/ForwardModal/ForwardShareStore';
import { isShareActionType, ShareActionType } from '@/forwarding/constants/ShareActionType';
import { RecentDestination } from '@/forwarding/types/RecentDestination';
import { RecentDestinationId } from '@/forwarding/types/RecentDestinationId';
import { getShareActionTitle } from '@/forwarding/utils/getShareActionTitle';
import UserStore from '@/user/stores/UserStore';

class RecentDestinationsStore {
  private recentDestinations: Nullable<RecentDestinationId[]> = null;

  filter: string = '';

  private command: Nullable<DriveDestinationCommand> = null;

  private isCollectionDestinationOnly = false;

  private forwardedItemId: Nullable<DriveItemId> = null;

  private selectedDestinationId: Nullable<DriveTreeItem['id']> = null;

  constructor(
    private userStore: UserStore,
    private driveTreeStore: DriveTreeStore,
    private forwardShareStore: ForwardShareStore,
  ) {
    makeAutoObservable(this);
  }

  get allDestinations(): Nullable<RecentDestination[]> {
    if (!this.recentDestinations || !this.command || !this.driveTreeStore.tree) {
      return null;
    }

    const treeNodes = flattenTree(this.driveTreeStore.tree)
      .map((item) => item.original)
      .filter((node) => !isTemporaryDriveItemId(node.id as number));

    const treeNodesMap = new Map<DriveItemId, DriveNode>(treeNodes.map((node) => [node.id as number, node]));

    const shareActions = new Set<ShareActionType>(this.forwardShareStore.actions);

    const restIds = [...treeNodes].sort(sortByCreatedAt).map((node) => node.id);

    // recent locations first, then the drive root, then the rest by creation date, then share actions;
    const ids = [
      ...new Set<RecentDestinationId>([...this.recentDestinations, null, ...restIds, ...shareActions]),
    ];

    return this.filterAllowedIds(ids, treeNodesMap).flatMap((destination) => {
      const data = this.toDestination(destination, treeNodesMap, shareActions, this.command!);

      return data ? [data] : [];
    });
  }

  get destinations(): Nullable<RecentDestination[]> {
    if (!this.allDestinations) {
      return null;
    }

    const filter = this.filter.trim().toLowerCase();

    if (filter.length === 0) {
      return this.allDestinations;
    }

    return this.allDestinations.filter((destination) => {
      const title = this.getDestinationTitle(destination);

      return title.toLowerCase().includes(filter);
    });
  }

  get isLoading(): boolean {
    return this.recentDestinations !== null && !this.driveTreeStore.tree && this.driveTreeStore.isLoading;
  }

  get selected(): Nullable<DriveTreeItem> {
    if (!this.allDestinations || !this.selectedDestinationId) {
      return null;
    }

    const driveDestinations = this.allDestinations.filter(
      (destination): destination is DriveTreeItem => !isShareActionType(destination),
    );

    return driveDestinations.find((destination) => destination.id === this.selectedDestinationId) ?? null;
  }

  changeFilter = (filter: string) => {
    this.filter = filter;
  };

  refresh = async (
    command: DriveDestinationCommand,
    isCollectionDestinationOnly = false,
    forwardedItemId: Nullable<DriveItemId> = null,
  ) => {
    this.command = command;
    this.isCollectionDestinationOnly = isCollectionDestinationOnly;
    this.forwardedItemId = forwardedItemId;
    this.selectedDestinationId = null;

    if (!this.userStore.user?.hasRollout(Rollout.Drive)) {
      this.recentDestinations = [];

      return;
    }

    this.recentDestinations = RecentDestinationStorage.get();

    await this.driveTreeStore.refresh();
  };

  reset = () => {
    this.filter = '';
    this.recentDestinations = null;
    this.command = null;
    this.isCollectionDestinationOnly = false;
    this.forwardedItemId = null;
    this.selectedDestinationId = null;
  };

  add = (destination: RecentDestinationId) => {
    RecentDestinationStorage.add(destination);

    if (this.recentDestinations !== null) {
      this.recentDestinations = RecentDestinationStorage.get();
    }
  };

  setSelectedDestination = (destination: Nullable<DriveTreeItem>) => {
    this.selectedDestinationId = destination?.id ?? null;
  };

  private getDestinationTitle(destination: RecentDestination): string {
    return isShareActionType(destination)
      ? getShareActionTitle(destination, this.forwardShareStore.hasMultipleUrls)
      : destination.title;
  }

  private filterAllowedIds(ids: RecentDestinationId[], nodesMap: Map<DriveItemId, DriveNode>): RecentDestinationId[] {
    const forwardedSubtreeIds = this.getForwardedSubtreeIds();
    const allowedIds = ids.filter((id) => typeof id !== 'number' || !forwardedSubtreeIds.has(id));

    if (this.isCollectionDestinationOnly) {
      return allowedIds.filter(
        (id) => isShareActionType(id) || (id !== null && nodesMap.get(id)?.type === DriveItemType.Collection),
      );
    } else {
      return allowedIds;
    }
  }

  private getForwardedSubtreeIds(): Set<StringOrNumber> {
    if (this.forwardedItemId === null || !this.driveTreeStore.tree) {
      return new Set();
    }

    return new Set(collectSubtreeIds(this.driveTreeStore.tree, this.forwardedItemId));
  }

  private toDestination(
    destination: RecentDestinationId,
    nodesMap: Map<DriveItemId, DriveNode>,
    shareActions: Set<ShareActionType>,
    command: DriveDestinationCommand,
  ): Nullable<RecentDestination> {
    if (isShareActionType(destination)) {
      return shareActions.has(destination) ? destination : null;
    }

    if (destination === null) {
      return new AppSectionTreeItem({
        id: DriveSection.DriveRoot,
        title: DRIVE_ROOT_NAME,
        collapsed: false,
      });
    }

    const node = nodesMap.get(destination)!;

    if (!node) {
      return null;
    }

    if (node.type === DriveItemType.Folder) {
      return new FolderTreeItem(node);
    }

    const addToCollectionRequest = toCommandCollectionRequest(command);

    if (
      addToCollectionRequest &&
      node.type === DriveItemType.Collection &&
      node.collectionId &&
      addToCollectionRequest.productType === toBaseProductType(node.collectionType)
    ) {
      return new CollectionTreeItem(node, false);
    }

    return null;
  }
}

export const useRecentDestinationsStore = () => useStore(RecentDestinationsStore);

export default RecentDestinationsStore;
